import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Formik, useFormikContext } from 'formik';
import isEqual from 'react-fast-compare';
import _ from 'lodash';
import {
  GO_STEP,
  NEXT_STEP,
  SET_HAS_SUBMITTED,
  SET_LAST_VISITED_STEP,
  SET_DEFAULT_STEPS,
} from './constants';
import {
  useWizardState,
  useWizardDispatch,
  WizardProvider,
} from './WizardContext';
import {
  NavigationControls,
  NextButton,
  SubmitButton,
} from './Navigation';
import GoToStepContext from './Navigation/GoToStep';
import { useAppContext } from '../../context/appContext';

const FormWizardRoot = () => {
  const dispatch = useWizardDispatch();
  const { totalSteps, currentStep, defaultSteps } = useWizardState();
  const { store: { submitted: hasSubmitted } } = useAppContext();
  const {
    initialValues, values, touched,
  } = useFormikContext();

  useEffect(() => {
    if (hasSubmitted) {
      dispatch({ type: GO_STEP, step: totalSteps });
    }
  }, []);

  useEffect(() => {
    dispatch({
      type: SET_DEFAULT_STEPS,
      defaultSteps: defaultSteps.map((element, index) => {
        if (currentStep > index || currentStep === totalSteps) {
          element.isCompleted = true;
        }
        return { ...element };
      }),
    });
  }, [currentStep, hasSubmitted]);

  useEffect(() => {
    if (values && !isEqual(initialValues, values)) {
      dispatch({
        type: SET_DEFAULT_STEPS,
        defaultSteps: defaultSteps.map((element, index) => {
          if (currentStep <= index) {
            element.isCompleted = false;
            return { ...element };
          }
          return { ...element };
        }),
      });
    }
    if (!_.isEmpty(touched) && !isEqual(initialValues, values)) {
      dispatch({ type: SET_LAST_VISITED_STEP });
    }
  }, [values]);

  return null;
};

FormWizardRoot.propTypes = {};

const FormikWizard = ({
  children,
  validations,
  validationsSchema,
  handleSubmit,
}) => {
  const state = useWizardState();
  const dispatch = useWizardDispatch();

  const handleOnSubmit = async (values, actions) => {
    const lastStepBasedOnSubmit = state.submitOnStep || state.isLastPage;
    if (
      lastStepBasedOnSubmit === state.currentStep
      && state.currentStep === state.totalSteps
    ) {
      if (handleSubmit) {
        actions.setSubmitting(false);
        const isSubmitted = await handleSubmit(values, actions);
        if (!state.hasSubmitted) {
          dispatch({ type: SET_HAS_SUBMITTED, payload: true });
        }

        if (isSubmitted && !state.isLastPage) {
          actions.setTouched({});
          actions.setSubmitting(false);
          dispatch({ type: NEXT_STEP, values });
        }
      }
    } else {
      actions.setTouched({});
      actions.setSubmitting(false);
      dispatch({ type: NEXT_STEP, values });
    }
  };

  return (
    <Formik
      enableReinitialize
      initialValues={state.initialValues}
      validationSchema={validationsSchema ? validationsSchema[state.currentStep] : null}
      validate={validations ? validations[state.currentStep] : null}
      validateOnBlur
      onSubmit={handleOnSubmit}
    >
      {(formikProps) => (
        <>
          <FormWizardRoot />
          {children({ ...state, ...formikProps, validationsSchemas: validationsSchema })}
        </>
      )}
    </Formik>
  );
};

FormikWizard.propTypes = {
  children: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  handleSubmit: PropTypes.func,
  validations: PropTypes.oneOfType([PropTypes.shape(), PropTypes.array]),
  validationsSchema: PropTypes.oneOfType([PropTypes.shape(), PropTypes.arrayOf(PropTypes.func)]),
};

FormikWizard.defaultProps = {
  children: {},
  handleSubmit: undefined,
  validations: {},
  validationsSchema: undefined,
};

const FormWizard = ({
  children, initialValues, submitOnStep, ...props
}) => (
  <WizardProvider {...props} initialValues={initialValues} submitOnStep={submitOnStep}>
    <FormikWizard {...props}>{children}</FormikWizard>
  </WizardProvider>
);

FormWizard.propTypes = {
  children: PropTypes.func,
  initialValues: PropTypes.shape(),
  submitOnStep: PropTypes.number.isRequired,
  validationsSchema: PropTypes.oneOfType([PropTypes.any]),
};

FormWizard.defaultProps = {
  children: {},
  initialValues: undefined,
  validationsSchema: undefined,
};

FormWizard.NavigationControls = NavigationControls;
FormWizard.Next = NextButton;
FormWizard.Submit = SubmitButton;
FormWizard.Stepper = GoToStepContext;

export default FormWizard;
